import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import { useLongPress } from "@/hooks/use-long-press";
import {
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  Columns2,
  GripVertical,
  ChevronsUpDown,
  Maximize2,
  Minimize2,
  Minus,
  Rows2,
  X,
} from "lucide-react";
import type { SidebarView } from "@/components/board-sidebar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Hint } from "@/components/ui/tooltip";
import {
  dropTargetAt,
  edgePreviewRect,
  previewRect,
  sameTarget,
  tabShift,
  tabSlotAt,
  type DropGeometry,
  type DropTarget,
  type Rect,
} from "@/lib/drop-zone";
import { hasViewDrag, isEditableTarget, readViewDrag } from "@/lib/window-intents";
import { arrange, type Gutter } from "@/lib/workspace-arrange";
import {
  activateTab,
  activeTabIndex,
  addTab,
  canMinimize,
  closeActive,
  closeTab,
  closeWindow,
  cycleTab,
  findView,
  moveTab,
  reorderTab,
  swapWindows,
  tabsOf,
  equalize,
  focus,
  focusDirection,
  focusIndex,
  moveToEdge,
  moveWindow,
  fitWindows,
  isEmptyWindowKey,
  isMinimizeKey,
  minimize,
  openEmptyWindow,
  openView,
  resize,
  restore,
  toggleMaximize,
  unmaximize,
  viewKey,
  windows,
  type Edge,
  type WindowLeaf,
  type Workspace,
} from "@/lib/workspace";
import { cn } from "@/lib/utils";


/** Keys that arrange windows; listed in the Windows menu. `Alt` because Ctrl/Cmd+W belongs to the browser. */
export const WINDOW_SHORTCUTS: Array<[string, string]> = [
  ["Alt+\\", "Split right"],
  ["Alt+Shift+\\", "Split below"],
  ["Alt+N", "New empty window"],
  ["Alt+W", "Close tab (a window's last closes it)"],
  ["Ctrl+Tab", "Next tab"],
  ["Ctrl+Shift+Tab", "Previous tab"],
  ["Alt+Shift+] / [", "Next / previous tab"],
  ["Alt+1…9", "Focus window n"],
  ["Alt+Shift+Arrows", "Move focus"],
  ["Alt+Shift+Z", "Maximise / restore"],
  ["Alt+M", "Minimise / restore"],
  ["Alt+L", "Next saved layout"],
  ["Alt+Shift+L", "Previous saved layout"],
  [/Mac|iPhone|iPad/.test(globalThis.navigator?.userAgent ?? "") ? "⌘⇧K" : "Ctrl+Shift+K", "Focus the window's message box"],
  ["[", "Navigation: fold, or bring back"],
];

type DragTarget = DropTarget;

/** A window (no `tab`) or a tab being dragged: what the ghost shows, and where it would land. */
interface DragState {
  id: string;
  tab?: number;
  title: string;
  icon: ReactNode;
  target: DropTarget | null;
  /** Over its own strip: the slot it would drop into. */
  slot: number | null;
  /** Every tab's width in its strip when the drag began: the gap the others open, and how far the dragged one moves into it. */
  tabWidths?: number[];
}

const targetKey = (target: DropTarget) => (target.kind === "edge" ? `edge:${target.edge}` : `${target.id}:${target.zone}`);

export function WorkspaceView({
  ws,
  apply,
  titleOf,
  iconOf,
  renderWindow,
  now,
}: {
  ws: Workspace;
  apply: (op: (ws: Workspace) => Workspace) => void;
  titleOf: (view: SidebarView) => string;
  iconOf: (view: SidebarView) => ReactNode;
  /** One pane per tab: `window.view` is that tab's view; `active` is false for a tab kept mounted behind another. */
  renderWindow: (window: WindowLeaf, focused: boolean, active: boolean) => ReactNode;
  now: () => number;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const element = root.current;
    if (!element) return;
    const measure = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const wsRef = useRef(ws);
  wsRef.current = ws;
  const list = windows(ws);
  // A window was added and they no longer all fit at their minimum: re-tile.
  const seenCount = useRef(list.length);
  useLayoutEffect(() => {
    const grew = list.length > seenCount.current;
    seenCount.current = list.length;
    if (grew && size.width > 0) apply((current) => fitWindows(current, size.width, size.height));
  }, [list.length, size.width, size.height, apply]);
  const single = list.length === 1;
  // In pixels: a minimised window is a fixed bar, not a fraction. Windows are drawn flat,
  // positioned absolutely and keyed by id, so a split or a move never remounts one.
  const { frames, gutters } = arrange(ws.root, size);
  const maximized = ws.maximizedId;

  /** Minimise, or restore a bar; the last open window says why it stays. */
  const toggleMin = (id: string) => {
    const current = wsRef.current;
    const target = windows(current).find((window) => window.id === id);
    if (target?.minimized) return apply((ws) => restore(ws, id));
    if (!canMinimize(current, id)) {
      toast("The last window stays open", { id: "minimise-last" });
      return;
    }
    apply((ws) => minimize(ws, id));
  };

  /* ---- where a drag would land: a workspace edge first, else a window's zone ---- */
  const rootRect = (): Rect => {
    const r = root.current!.getBoundingClientRect();
    return { left: r.left, top: r.top, width: r.width, height: r.height };
  };
  /** The workspace in client coordinates, measured once when a drag starts (and again on a resize), never per move. */
  const measure = (): DropGeometry => {
    const r = rootRect();
    return {
      root: r,
      windows: list.map((window) => {
        const box = frames.get(window.id)!;
        return { id: window.id, rect: { left: r.left + box.x, top: r.top + box.y, width: box.w, height: box.h }, bar: Boolean(box.bar) };
      }),
      maximizedId: maximized,
      single,
    };
  };
  const measureRef = useRef(measure);
  measureRef.current = measure;
  /** The preview's box in the workspace's own coordinates, from the frames: no DOM read. */
  const previewOf = (target: DropTarget | null): Rect | null => {
    if (!target) return null;
    const whole: Rect = { left: 0, top: 0, width: size.width, height: size.height };
    if (target.kind === "edge") return edgePreviewRect(whole, target.edge);
    const box = frames.get(target.id);
    if (!maximized && !box) return null;
    return previewRect(maximized || !box ? whole : { left: box.x, top: box.y, width: box.w, height: box.h }, target.zone);
  };

  /*
   * A drag in progress. Only what changes the picture is React state — which window or
   * tab, and the target or slot, which change a few times a drag. The pointer itself is
   * read once per animation frame and moves the ghost directly, so the rest of the
   * workspace does not re-render as the mouse moves.
   */
  const [drag, setDrag] = useState<DragState | null>(null);
  const ghost = useRef<HTMLDivElement>(null);
  const pointer = useRef({ x: 0, y: 0 });
  const moveGhost = (x: number, y: number) => {
    ghost.current?.style.setProperty("transform", `translate(${x + 14}px, ${y + 12}px)`);
  };

  /**
   * Follows one pointer from a press: past 6px it is a drag (`start`), then `frame`
   * runs at most once per animation frame with the latest position, and `end` once,
   * committing on release and not on Escape, a cancel or `cancelled()`.
   */
  const track = (
    event: React.PointerEvent,
    handlers: { start: () => void; frame: (x: number, y: number) => void; end: (commit: boolean) => void; cancelled?: () => boolean },
  ) => {
    const startX = event.clientX;
    const startY = event.clientY;
    const handle = event.currentTarget as HTMLElement;
    const pointerId = event.pointerId;
    let active = false;
    let raf = 0;
    const flush = () => {
      raf = 0;
      handlers.frame(pointer.current.x, pointer.current.y);
    };
    const onMove = (move: PointerEvent) => {
      if (move.pointerId !== pointerId) return;
      if (handlers.cancelled?.()) return finish(false);
      if (!active) {
        if (Math.hypot(move.clientX - startX, move.clientY - startY) < 6) return;
        active = true;
        handle.setPointerCapture?.(pointerId);
        pointer.current = { x: move.clientX, y: move.clientY };
        handlers.start();
      }
      move.preventDefault();
      pointer.current = { x: move.clientX, y: move.clientY };
      if (!raf) raf = requestAnimationFrame(flush);
    };
    const finish = (commit: boolean) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("keydown", onKey);
      if (raf) {
        cancelAnimationFrame(raf);
        if (active && commit) flush();
        raf = 0;
      }
      if (active) handlers.end(commit);
    };
    const onUp = (up: PointerEvent) => up.pointerId === pointerId && finish(true);
    const onCancel = () => finish(false);
    const onKey = (key: KeyboardEvent) => key.key === "Escape" && finish(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    window.addEventListener("keydown", onKey);
  };

  /**
   * A drop: one change of layout, the windows easing into their new frames (a moment
   * of `motion-settle`), and a re-tile folded into the same change when a window was
   * added and they no longer fit — never a second jump after the first.
   */
  const [settling, setSettling] = useState(false);
  const settleTimer = useRef(0);
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const land = (op: (ws: Workspace) => Workspace) => {
    window.clearTimeout(settleTimer.current);
    setSettling(true);
    settleTimer.current = window.setTimeout(() => setSettling(false), 220);
    apply((current) => {
      const next = op(current);
      const { width, height } = sizeRef.current;
      return windows(next).length > windows(current).length && width > 0 ? fitWindows(next, width, height) : next;
    });
  };
  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  /* ---- a window dragged by its title bar ---- */
  /** `held` turns true once a finger's hold has opened the window's menu: the press is then the menu's, not a drag. */
  const startTitleDrag = (id: string, event: React.PointerEvent, held: () => boolean) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest("button")) return;
    const view = windows(wsRef.current).find((window) => window.id === id)?.view;
    if (!view) return;
    let geo: DropGeometry | null = null;
    let target: DropTarget | null = null;
    const remeasure = () => (geo = measureRef.current());
    track(event, {
      cancelled: held,
      start: () => {
        remeasure();
        window.addEventListener("resize", remeasure);
        setDrag({ id, title: titleOf(view), icon: iconOf(view), target: null, slot: null });
      },
      frame: (x, y) => {
        moveGhost(x, y);
        let next = geo ? dropTargetAt(geo, x, y, target) : null;
        if (next?.kind === "window" && next.id === id) next = null;
        if (sameTarget(next, target)) return;
        target = next;
        setDrag((current) => current && { ...current, target: next });
      },
      end: (commit) => {
        window.removeEventListener("resize", remeasure);
        setDrag(null);
        const landed = target;
        if (!commit || !landed) return;
        land((current) => (landed.kind === "edge" ? moveToEdge(current, id, landed.edge) : moveWindow(current, id, landed.id, landed.zone)));
      },
    });
  };

  /* ---- a tab dragged: along its strip it reorders, out of it it lands like a window ---- */
  const startTabDrag = (id: string, index: number, event: React.PointerEvent) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest("button")) return;
    event.stopPropagation();
    // A press shows the tab at once, as VS Code does; moving it is a drag.
    apply((current) => activateTab(current, id, index));
    const strip = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-tab-strip]");
    let geo: DropGeometry | null = null;
    let stripBox: Rect | null = null;
    let tabRects: Array<{ left: number; width: number }> = [];
    let target: DropTarget | null = null;
    let slot: number | null = null;
    const remeasure = () => {
      geo = measureRef.current();
      const box = strip?.getBoundingClientRect();
      stripBox = box ? { left: box.left, top: box.top, width: box.width, height: box.height } : null;
      // The strip as it stands before any tab slides aside: the slots are read against these.
      tabRects = strip
        ? [...strip.querySelectorAll<HTMLElement>("[data-tab]")].map((tab) => {
            const r = tab.getBoundingClientRect();
            return { left: r.left, width: r.width };
          })
        : [];
    };
    track(event, {
      start: () => {
        remeasure();
        window.addEventListener("resize", remeasure);
        const owner = windows(wsRef.current).find((window) => window.id === id);
        const view = owner ? tabsOf(owner)[index] : undefined;
        if (!view) return;
        setDrag({ id, tab: index, title: titleOf(view), icon: iconOf(view), target: null, slot: null, tabWidths: tabRects.map((tab) => tab.width) });
      },
      frame: (x, y) => {
        moveGhost(x, y);
        const box = stripBox;
        const onStrip = box !== null && y >= box.top - 12 && y <= box.top + box.height + 12 && x >= box.left - 12 && x <= box.left + box.width + 12;
        const nextSlot = onStrip ? tabSlotAt(tabRects, x) : null;
        let next = nextSlot === null && geo ? dropTargetAt(geo, x, y, target) : null;
        // Its own window's centre is where it already is.
        if (next?.kind === "window" && next.id === id && next.zone === "center") next = null;
        if (nextSlot === slot && sameTarget(next, target)) return;
        slot = nextSlot;
        target = next;
        setDrag((current) => current && { ...current, slot: nextSlot, target: next });
      },
      end: (commit) => {
        window.removeEventListener("resize", remeasure);
        // In the same render as the move, so the tabs that slid aside are where the
        // reorder puts them: the gap closes with no jump.
        setDrag(null);
        if (!commit) return;
        const landedSlot = slot;
        const landed = target;
        if (landedSlot !== null) apply((current) => reorderTab(current, id, index, landedSlot > index ? landedSlot - 1 : landedSlot));
        else if (landed) land((current) => moveTab(current, id, index, landed));
      },
    });
  };

  /* ---- a view dragged in from outside: a board out of the nav, an agent out of its list ---- */
  const [incoming, setIncoming] = useState<DropTarget | null>(null);
  // HTML5 drag events come at the browser's own pace; the geometry is still measured once per drag.
  const incomingGeo = useRef<DropGeometry | null>(null);
  const incomingTarget = useRef<DropTarget | null>(null);
  const showIncoming = (target: DropTarget | null) => {
    if (sameTarget(target, incomingTarget.current)) return;
    incomingTarget.current = target;
    setIncoming(target);
  };
  const endIncoming = () => {
    incomingGeo.current = null;
    showIncoming(null);
  };
  const dropHandlers = {
    onDragOver: (event: React.DragEvent) => {
      if (!hasViewDrag(event.dataTransfer)) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      incomingGeo.current ??= measure();
      showIncoming(dropTargetAt(incomingGeo.current, event.clientX, event.clientY, incomingTarget.current));
    },
    onDragLeave: (event: React.DragEvent) => {
      if (!root.current?.contains(event.relatedTarget as Node | null)) endIncoming();
    },
    onDrop: (event: React.DragEvent) => {
      const view = readViewDrag(event.dataTransfer);
      const target = incomingTarget.current ?? dropTargetAt(incomingGeo.current ?? measure(), event.clientX, event.clientY);
      endIncoming();
      if (!view || !target) return;
      event.preventDefault();
      land((current) => placeView(current, view, target));
    },
  };
  const endIncomingRef = useRef(endIncoming);
  endIncomingRef.current = endIncoming;
  useEffect(() => {
    // A drag that ends anywhere (Esc, a drop outside) clears the preview.
    const clear = () => endIncomingRef.current();
    window.addEventListener("dragend", clear);
    window.addEventListener("drop", clear);
    return () => {
      window.removeEventListener("dragend", clear);
      window.removeEventListener("drop", clear);
    };
  }, []);

  /* ---- gutters: drag, arrows when focused, double-click for an equal split ---- */
  const startGutterDrag = (gutter: Gutter, event: React.PointerEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture?.(event.pointerId);
    const r = rootRect();
    const start = gutter.dir === "row" ? r.left : r.top;
    // The pointer in pixels, as a boundary in the split's stored fractions (minimised siblings take no share of the drag).
    const onMove = (move: PointerEvent) => {
      const position = (gutter.dir === "row" ? move.clientX : move.clientY) - start;
      apply((current) => resize(current, gutter.path, gutter.index, gutter.startFrac + (position - gutter.origin) / gutter.unit, gutter.unit));
    };
    const onUp = () => {
      handle.removeEventListener("pointermove", onMove);
      handle.removeEventListener("pointerup", onUp);
      handle.removeEventListener("pointercancel", onUp);
    };
    handle.addEventListener("pointermove", onMove);
    handle.addEventListener("pointerup", onUp);
    handle.addEventListener("pointercancel", onUp);
  };
  const nudgeGutter = (gutter: Gutter, event: React.KeyboardEvent) => {
    const back = gutter.dir === "row" ? "ArrowLeft" : "ArrowUp";
    const forward = gutter.dir === "row" ? "ArrowRight" : "ArrowDown";
    if (event.key !== back && event.key !== forward) return;
    event.preventDefault();
    const step = event.shiftKey ? 0.1 : 0.02;
    apply((current) => resize(current, gutter.path, gutter.index, gutter.at + (event.key === forward ? step : -step), gutter.unit));
  };

  /* ---- keys ---- */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      // Ctrl+Tab / Ctrl+Shift+Tab cycle the focused window's tabs (where the browser lets the page have them).
      if (event.ctrlKey && !event.metaKey && !event.altKey && event.key === "Tab" && tabsOf(focusedLeaf(wsRef.current)).length > 1) {
        event.preventDefault();
        apply((ws) => cycleTab(ws, event.shiftKey ? -1 : 1));
        return;
      }
      if (event.ctrlKey || event.metaKey) return;
      const current = wsRef.current;
      if (event.key === "Escape" && current.maximizedId && !document.querySelector("[role=dialog],[role=menu]")) {
        apply(unmaximize);
        return;
      }
      if (!event.altKey) return;
      const id = current.focusedId;
      const code = event.code;
      let op: ((ws: Workspace) => Workspace) | null = null;
      const keyEvent = { code, altKey: true, shiftKey: event.shiftKey, ctrlKey: false, metaKey: false, editable: isEditableTarget(event.target) };
      if (isEmptyWindowKey(keyEvent)) {
        op = openEmptyWindow;
      } else if (isMinimizeKey(keyEvent)) {
        event.preventDefault();
        toggleMin(id);
        return;
      } else if (code === "Backslash") {
        const view = windows(current).find((window) => window.id === id)?.view;
        if (view) op = (ws) => openView(ws, view, { kind: "split", targetId: id, zone: event.shiftKey ? "bottom" : "right" }, { duplicate: true });
      } else if (code === "KeyW" && !event.shiftKey) op = (ws) => closeActive(ws, now());
      else if (event.shiftKey && (code === "BracketRight" || code === "BracketLeft") && !keyEvent.editable) {
        op = (ws) => cycleTab(ws, code === "BracketRight" ? 1 : -1);
      }
      else if (code === "KeyZ" && event.shiftKey) op = (ws) => toggleMaximize(ws, id);
      else if (/^Digit[1-9]$/.test(code) && !event.shiftKey) op = (ws) => focusIndex(ws, Number(code.slice(5)));
      else if (event.shiftKey && code.startsWith("Arrow")) {
        const dir = ({ ArrowLeft: "left", ArrowRight: "right", ArrowUp: "top", ArrowDown: "bottom" } as const)[
          code as "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown"
        ];
        if (dir) op = (ws) => focusDirection(ws, dir);
      }
      if (!op) return;
      event.preventDefault();
      apply(op);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [apply, now]);

  const previewTarget = drag ? drag.target : incoming;
  const preview = previewOf(previewTarget);

  return (
    <div ref={root} data-workspace className="relative min-h-0 min-w-0 flex-1 overflow-hidden" {...dropHandlers}>
      {list.map((window, index) => {
        const box = frames.get(window.id)!;
        const isMax = maximized === window.id;
        const hidden = maximized !== null && !isMax;
        const focused = window.id === ws.focusedId;
        const bar = isMax ? null : box.bar;
        const tabs = tabsOf(window);
        const activeTab = activeTabIndex(window);
        const tabDrag = drag && drag.id === window.id && drag.tab !== undefined ? drag : null;
        return (
          <section
            key={window.id}
            data-window={window.id}
            aria-label={titleOf(window.view)}
            // The others stay mounted under a maximised one, so nothing in them is lost.
            aria-hidden={hidden || undefined}
            // React 18's types predate `inert`; the attribute keeps focus out of a hidden window.
            {...(hidden ? ({ inert: "" } as Record<string, string>) : {})}
            className={cn("absolute flex flex-col", !single && !isMax && "p-[3px]", hidden && "invisible", isMax && "z-20", settling && "motion-settle")}
            style={isMax ? { inset: 0 } : { left: box.x, top: box.y, width: box.w, height: box.h }}
            onPointerDownCapture={() => !focused && apply((ws) => focus(ws, window.id))}
            onFocusCapture={() => !focused && apply((ws) => focus(ws, window.id))}
          >
            <div
              className={cn(
                "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background",
                !single && "rounded-lg border",
                // Focus is a quiet tint of the ring in the border, not a second hard outline:
                // enough to tell where keys go, never louder than what the windows hold.
                !single &&
                  (focused
                    ? "border-[color-mix(in_oklab,var(--ring)_45%,var(--border))] shadow-[0_1px_8px_-3px_color-mix(in_oklab,var(--ring)_30%,transparent)]"
                    : "border-border/80"),
                drag?.id === window.id && drag.tab === undefined && "opacity-60",
              )}
            >
              {/* One window has no title bar, as before windows had any — unless it holds tabs, whose strip it needs. */}
              {single && !isMax && tabs.length < 2 ? null : (
                <TitleBar
                  title={titleOf(window.view)}
                  tabs={tabs.length > 1 ? tabs.map((tab) => ({ key: viewKey(tab), title: titleOf(tab), icon: iconOf(tab) })) : null}
                  activeTab={activeTab}
                  tabGap={tabDrag ? { from: tabDrag.tab!, slot: tabDrag.slot, widths: tabDrag.tabWidths ?? [] } : null}
                  onActivateTab={(tab) => apply((ws) => activateTab(ws, window.id, tab))}
                  onCloseTab={(tab) => apply((ws) => closeTab(ws, window.id, tab, now()))}
                  onTabPointerDown={(tab, event) => startTabDrag(window.id, tab, event)}
                  icon={iconOf(window.view)}
                  index={index + 1}
                  focused={focused}
                  maximized={isMax}
                  bar={bar}
                  onMinimize={() => toggleMin(window.id)}
                  others={list.filter((other) => other.id !== window.id).map((other) => ({ id: other.id, title: titleOf(other.view) }))}
                  onPointerDown={(event, held) => startTitleDrag(window.id, event, held)}
                  onSplit={(zone) =>
                    apply((ws) => openView(ws, window.view, { kind: "split", targetId: window.id, zone }, { duplicate: true }))
                  }
                  onMaximize={() => apply((ws) => toggleMaximize(ws, window.id))}
                  onMoveToEdge={(edge) => apply((ws) => moveToEdge(ws, window.id, edge))}
                  onSwap={(other) => apply((ws) => swapWindows(ws, window.id, other))}
                  onClose={() => apply((ws) => closeWindow(ws, window.id, now()))}
                />
              )}
              {/* A bar keeps its body mounted, hidden: a chat keeps its draft, a board its scroll. So does
                  a tab behind another — at full size, invisible and with its rendering skipped
                  (content-visibility), so it keeps its state but costs no layout or paint while hidden. */}
              <div className={cn("relative flex min-h-0 min-w-0 flex-1 flex-col", bar && "hidden")}>
                {tabs.map((tab, index) => {
                  const shown = index === activeTab;
                  return (
                    <div
                      key={viewKey(tab)}
                      className={cn("absolute inset-0 flex min-h-0 min-w-0 flex-col", !shown && "invisible [content-visibility:hidden]")}
                      aria-hidden={!shown || undefined}
                      {...(!shown ? ({ inert: "" } as Record<string, string>) : {})}
                    >
                      {renderWindow(shown ? window : { ...window, view: tab }, focused && shown, shown)}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      {maximized
        ? null
        : gutters.map((gutter) => {
            const row = gutter.dir === "row";
            const style = row
              ? { left: gutter.pos - 4, top: gutter.split.y, height: gutter.split.h, width: 8 }
              : { top: gutter.pos - 4, left: gutter.split.x, width: gutter.split.w, height: 8 };
            // Where gutters cross, the outer split's is on top: it is the one spanning the crossing.
            const zIndex = 20 - gutter.path.length;
            return (
              <div
                key={gutter.key}
                role="separator"
                aria-orientation={row ? "vertical" : "horizontal"}
                aria-label="Resize windows (arrows; double-click for equal sizes)"
                aria-valuenow={Math.round(gutter.at * 100)}
                tabIndex={0}
                className={cn(
                  "group absolute touch-none outline-none",
                  row ? "cursor-col-resize" : "cursor-row-resize",
                )}
                style={{ ...style, zIndex }}
                onPointerDown={(event) => startGutterDrag(gutter, event)}
                onDoubleClick={() => apply((ws) => equalize(ws, gutter.path))}
                onKeyDown={(event) => nudgeGutter(gutter, event)}
              >
                <span
                  className={cn(
                    "absolute rounded-full bg-ring/0 transition-colors group-hover:bg-ring/50 group-focus-visible:bg-ring",
                    row ? "inset-y-2 left-[3px] w-0.5" : "inset-x-2 top-[3px] h-0.5",
                  )}
                />
              </div>
            );
          })}

      {preview && previewTarget ? (
        <div
          // Keyed by its zone: a new zone is a new preview fading in where it is, never
          // the old one sliding across the window to get there.
          key={targetKey(previewTarget)}
          aria-hidden
          className={cn(
            "motion-drop pointer-events-none absolute z-30 flex items-center border-2 border-primary/70",
            // The centre adds a tab: the target's strip lights up, opaque, so its own title does not show through.
            previewTarget?.kind === "window" && previewTarget.zone === "center"
              ? "justify-center rounded-t-lg bg-[color-mix(in_oklab,var(--primary)_22%,var(--background))]"
              : "rounded-lg bg-primary/15",
          )}
          style={{ left: preview.left, top: preview.top, width: preview.width, height: preview.height }}
        >
          {previewTarget?.kind === "window" && previewTarget.zone === "center" ? (
            <span className="rounded bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground">Add as a tab</span>
          ) : null}
        </div>
      ) : null}

      {drag ? (
        // What is being dragged, following the pointer: moved by `moveGhost` once a frame, outside React.
        <div
          ref={ghost}
          aria-hidden
          className="pointer-events-none fixed left-0 top-0 z-50 flex max-w-[220px] items-center gap-1.5 rounded-md border border-border bg-elevated px-2 py-1 text-[12px] text-foreground opacity-60 shadow-lg [&_svg]:size-3.5"
          style={{ transform: `translate(${pointer.current.x + 14}px, ${pointer.current.y + 12}px)` }}
        >
          <span className="shrink-0 text-muted-foreground">{drag.icon}</span>
          <span className="truncate">{drag.title}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Puts a dropped view where it was dropped. A view already open (as a window or a
 * tab) is moved there rather than opened twice; the centre of a window adds it as a tab.
 */
export function placeView(ws: Workspace, view: SidebarView, target: DragTarget): Workspace {
  const found = findView(ws, view);
  if (target.kind === "window" && target.zone === "center") return addTab(ws, target.id, view);
  if (target.kind === "edge") {
    return found ? moveTab(ws, found.window.id, found.index, target) : openView(ws, view, { kind: "edge", edge: target.edge }, { duplicate: true });
  }
  if (found) {
    const alone = tabsOf(found.window).length < 2;
    return found.window.id === target.id && alone ? focus(ws, target.id) : moveTab(ws, found.window.id, found.index, target);
  }
  return openView(ws, view, { kind: "split", targetId: target.id, zone: target.zone as Edge }, { duplicate: true });
}

function focusedLeaf(ws: Workspace): WindowLeaf {
  return windows(ws).find((window) => window.id === ws.focusedId) ?? windows(ws)[0]!;
}

type TabInfo = { key: string; title: string; icon: ReactNode };
type TabGap = { from: number; slot: number | null; widths: number[] };

function TitleBar({
  title,
  tabs,
  activeTab,
  tabGap,
  onActivateTab,
  onCloseTab,
  onTabPointerDown,
  icon,
  index,
  focused,
  maximized,
  bar,
  onMinimize,
  others,
  onPointerDown,
  onSplit,
  onMaximize,
  onMoveToEdge,
  onSwap,
  onClose,
}: {
  title: string;
  /** A tab group's tabs (two or more), drawn as a strip in place of the title; null for a plain window. */
  tabs: TabInfo[] | null;
  activeTab: number;
  /** While one of its tabs is dragged: which, the slot it is over (null: off the strip) and every tab's width. */
  tabGap: TabGap | null;
  onActivateTab: (index: number) => void;
  onCloseTab: (index: number) => void;
  onTabPointerDown: (index: number, event: React.PointerEvent) => void;
  icon: ReactNode;
  index: number;
  focused: boolean;
  maximized: boolean;
  /** Minimised: drawn as a horizontal bar across a stack, or a vertical strip beside a window. */
  bar: "horizontal" | "vertical" | null;
  /** Minimise, or restore when minimised. */
  onMinimize: () => void;
  others: Array<{ id: string; title: string }>;
  /** Starts a title-bar drag; `held()` says a finger's hold has since opened the menu. */
  onPointerDown: (event: React.PointerEvent, held: () => boolean) => void;
  onSplit: (zone: Edge) => void;
  onMaximize: () => void;
  onMoveToEdge: (edge: Edge) => void;
  onSwap: (otherId: string) => void;
  onClose: () => void;
}) {
  const barRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState(false);
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  /** The menu was opened by a hold: whatever the finger does next is not a drag. */
  const heldRef = useRef(false);
  /** Opened from the keyboard: focus comes back to the bar when it closes. */
  const byKeyboard = useRef(false);
  /** Where a press began, so a click on a bar restores it but a drag of one does not. */
  const pressAt = useRef<{ x: number; y: number } | null>(null);
  /** When a click restored the bar: the double-click it is half of is not a maximise. */
  const restoredAt = useRef(0);
  const vertical = bar === "vertical";
  // A minimised group names its showing tab and how many it holds: "Board x · 3".
  const label = tabs && bar ? `${title} · ${tabs.length}` : title;
  const minimiseButton = (
    <Hint label={bar ? "Restore (Alt+M)" : "Minimise (Alt+M)"} side={vertical ? "right" : undefined}>
      <Button variant="ghost" size="icon-sm" className="size-6" aria-label={bar ? `Restore ${title}` : `Minimise ${title}`} onClick={onMinimize}>
        {bar ? <ChevronsUpDown className={cn(vertical && "rotate-90")} /> : <Minus />}
      </Button>
    </Hint>
  );
  const closeButton = (
    <Hint label={tabs ? "Close the window and its tabs — they stay under Windows → Closed" : "Close (Alt+W) — it stays under Windows → Closed"} side={vertical ? "right" : undefined}>
      <Button variant="ghost" size="icon-sm" className="size-6" aria-label={`Close ${title}`} onClick={onClose}>
        <X />
      </Button>
    </Hint>
  );
  const openAt = (point: { x: number; y: number }) => {
    const box = barRef.current?.getBoundingClientRect();
    setAnchor(
      box
        ? { x: Math.min(Math.max(point.x - box.left, 0), box.width), y: Math.min(Math.max(point.y - box.top, 0), box.height) }
        : { x: 0, y: 0 },
    );
    setMenu(true);
  };
  // Right-click, or a finger held still, opens the window's menu at the point. The
  // slop matches the drag's 6px, so a press that moves is a drag and never a menu.
  const hold = useLongPress({
    contextMenu: true,
    slop: 6,
    onLongPress: (point) => {
      heldRef.current = true;
      byKeyboard.current = false;
      openAt(point);
    },
  });
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    if ((event.key === "F10" && event.shiftKey) || event.key === "ContextMenu") {
      event.preventDefault();
      byKeyboard.current = true;
      const box = barRef.current?.getBoundingClientRect();
      openAt(box ? { x: box.left + 8, y: box.bottom } : { x: 0, y: 0 });
    }
  };
  return (
    // The menu is the bar's sibling, not its child: React events bubble out of a
    // portal along the component tree, and a press in the menu must not start a drag.
    <div className={cn("relative shrink-0", vertical && "flex min-h-0 flex-1")}>
      <div
        ref={barRef}
        tabIndex={0}
        role="group"
        aria-roledescription="window title bar"
        aria-label={`${title} window${bar ? ", minimised" : ""}. ${bar ? "Click to restore; drag" : "Drag"} to move; right-click, hold, or press Shift+F10 for its menu`}
        aria-keyshortcuts="Shift+F10 ContextMenu"
        className={cn(
          "flex touch-none select-none items-center outline-none [-webkit-touch-callout:none] active:cursor-grabbing",
          vertical ? "min-h-0 w-full flex-1 flex-col gap-1.5 py-1.5" : "h-8 gap-1.5 pl-1.5 pr-1",
          bar ? "cursor-pointer" : "cursor-grab border-b border-border/70",
          "focus-visible:shadow-[inset_0_0_0_2px_color-mix(in_oklab,var(--ring)_55%,transparent)]",
          focused ? "bg-[color-mix(in_oklab,var(--ring)_7%,var(--muted))]" : "bg-surface/50",
        )}
        {...hold}
        onPointerDown={(event) => {
          heldRef.current = false;
          pressAt.current = { x: event.clientX, y: event.clientY };
          // A hold on a button is that button's press, not the window's menu.
          if (!(event.target as HTMLElement).closest("button")) hold.onPointerDown(event);
          onPointerDown(event, () => heldRef.current);
        }}
        onKeyDown={onKeyDown}
        onClick={(event) => {
          // A plain click on a bar restores it; a drag, a hold or a button press does not.
          const from = pressAt.current;
          if (!bar || heldRef.current || (event.target as HTMLElement).closest("button")) return;
          if (from && Math.hypot(event.clientX - from.x, event.clientY - from.y) >= 6) return;
          restoredAt.current = Date.now();
          onMinimize();
        }}
        onDoubleClick={(event) =>
          !(event.target as HTMLElement).closest("button") && Date.now() - restoredAt.current > 500 && onMaximize()
        }
      >
        {vertical ? (
          <>
            <span aria-hidden className="shrink-0 text-muted-foreground [&_svg]:size-3.5">
              {icon}
            </span>
            <p
              className={cn(
                "min-h-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[12px] [writing-mode:vertical-rl]",
                focused ? "font-medium" : "text-muted-foreground",
              )}
            >
              {label}
            </p>
            {minimiseButton}
            {closeButton}
          </>
        ) : (
          <>
            <GripVertical aria-hidden className="size-3.5 shrink-0 text-muted-foreground/60" />
            {tabs && !bar ? (
              <TabStrip tabs={tabs} active={activeTab} focused={focused} gap={tabGap} onActivate={onActivateTab} onClose={onCloseTab} onPointerDown={onTabPointerDown} />
            ) : (
              <>
                <span aria-hidden className="shrink-0 text-muted-foreground [&_svg]:size-3.5">
                  {icon}
                </span>
                <p className={cn("min-w-0 flex-1 truncate text-[12px]", focused ? "font-medium" : "text-muted-foreground")}>{label}</p>
              </>
            )}
            {bar ? <span className="shrink-0 text-[10px] text-muted-foreground/70">minimised</span> : null}
            <span aria-hidden className="hidden shrink-0 text-[10px] tabular-nums text-muted-foreground/70 sm:inline">
              Alt+{index}
            </span>
            {minimiseButton}
            <Hint label={maximized ? "Restore (Esc)" : "Maximise (Alt+Shift+Z)"}>
              <Button variant="ghost" size="icon-sm" className="size-6" aria-label={maximized ? `Restore ${title}` : `Maximise ${title}`} onClick={onMaximize}>
                {maximized ? <Minimize2 /> : <Maximize2 />}
              </Button>
            </Hint>
            {closeButton}
          </>
        )}
      </div>
      <DropdownMenu open={menu} onOpenChange={setMenu}>
        <DropdownMenuTrigger asChild>
          <span aria-hidden tabIndex={-1} className="pointer-events-none absolute size-0" style={{ left: anchor.x, top: anchor.y }} />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-52"
          aria-label={`${title} window`}
          onCloseAutoFocus={(event) => {
            // The anchor is no place for focus; a keyboard user goes back to the bar.
            event.preventDefault();
            if (byKeyboard.current && barRef.current?.isConnected) barRef.current.focus({ preventScroll: true });
          }}
        >
          <DropdownMenuItem onSelect={() => onSplit("right")}>
            <Columns2 /> Split right
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onSplit("bottom")}>
            <Rows2 /> Split below
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onMinimize}>
            {bar ? <ChevronsUpDown /> : <Minus />} {bar ? "Restore" : "Minimise"}
            <span className="ml-auto text-[10.5px] text-muted-foreground">Alt+M</span>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={onMaximize}>
            {maximized ? <Minimize2 /> : <Maximize2 />} {maximized ? "Restore" : "Maximise"}
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ArrowRightToLine /> Move to…
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-52">
              <DropdownMenuItem onSelect={() => onMoveToEdge("left")}>
                <ArrowLeftToLine /> Left edge
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onMoveToEdge("right")}>
                <ArrowRightToLine /> Right edge
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onMoveToEdge("top")}>
                <ArrowUpToLine /> Top edge
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onMoveToEdge("bottom")}>
                <ArrowDownToLine /> Bottom edge
              </DropdownMenuItem>
              {others.length > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Swap with</DropdownMenuLabel>
                  {others.map((other) => (
                    <DropdownMenuItem key={other.id} onSelect={() => onSwap(other.id)}>
                      <span className="truncate">{other.title}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              ) : null}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onClose}>
            <X /> Close window <span className="ml-auto text-[10.5px] text-muted-foreground">Alt+W</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * A tab group's strip, VS Code's: icon and title per tab, the showing one in the
 * window's own background with its ×, the others dimmed (× on hover), scrolling
 * sideways rather than wrapping. A press shows a tab and may drag it; a middle click
 * or the × closes it.
 */
function TabStrip({
  tabs,
  active,
  focused,
  gap,
  onActivate,
  onClose,
  onPointerDown,
}: {
  tabs: TabInfo[];
  active: number;
  focused: boolean;
  gap: TabGap | null;
  onActivate: (index: number) => void;
  onClose: (index: number) => void;
  onPointerDown: (index: number, event: React.PointerEvent) => void;
}) {
  const strip = useRef<HTMLDivElement>(null);
  // The showing tab is kept in view as tabs come and go — scrolling the strip only,
  // never the page or the window around it, as scrollIntoView would.
  useLayoutEffect(() => {
    const box = strip.current;
    const tab = box?.querySelector<HTMLElement>(`[data-tab="${active}"]`);
    if (!box || !tab) return;
    const left = tab.offsetLeft;
    const right = left + tab.offsetWidth;
    if (left < box.scrollLeft) box.scrollLeft = left;
    else if (right > box.scrollLeft + box.clientWidth) box.scrollLeft = right - box.clientWidth;
  }, [active, tabs.length]);
  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      const next = (index + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
      onActivate(next);
      strip.current?.querySelector<HTMLElement>(`[data-tab="${next}"]`)?.focus();
    } else if (event.key === "Delete") {
      event.preventDefault();
      onClose(index);
    }
  };
  return (
    <div
      ref={strip}
      data-tab-strip
      role="tablist"
      aria-label="Tabs"
      className="relative -my-px flex h-8 min-w-0 flex-1 items-stretch overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      onWheel={(event) => {
        // A mouse wheel scrolls the strip sideways, as VS Code's does.
        if (event.deltaY && strip.current) strip.current.scrollLeft += event.deltaY;
      }}
    >
      {tabs.map((tab, index) => {
        const shown = index === active;
        const shift = gap ? tabShift(index, gap.from, gap.slot, gap.widths) : 0;
        return (
          <div
            key={tab.key}
            data-tab={index}
            role="tab"
            aria-selected={shown}
            tabIndex={shown ? 0 : -1}
            title={tab.title}
            className={cn(
              "group/tab relative flex max-w-[200px] shrink-0 cursor-pointer items-center gap-1.5 border-r border-border/60 pl-2.5 pr-1 text-[12px] outline-none",
              "focus-visible:shadow-[inset_0_0_0_2px_color-mix(in_oklab,var(--ring)_55%,transparent)]",
              shown
                ? cn("bg-background text-foreground", focused && "font-medium")
                : "text-muted-foreground hover:bg-background/50 hover:text-foreground",
              // While one is dragged the others slide aside to open its gap; the dragged one dims where it was.
              gap && "motion-tab-gap",
              gap?.from === index && "opacity-40",
            )}
            style={shift ? { transform: `translateX(${shift}px)` } : undefined}
            onPointerDown={(event) => onPointerDown(index, event)}
            onAuxClick={(event) => {
              if (event.button !== 1) return;
              event.preventDefault();
              onClose(index);
            }}
            onMouseDown={(event) => event.button === 1 && event.preventDefault()}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {/* The showing tab's top edge carries the window's accent. */}
            {shown ? <span aria-hidden className={cn("absolute inset-x-0 top-0 h-0.5", focused ? "bg-primary" : "bg-border")} /> : null}
            <span aria-hidden className={cn("shrink-0 [&_svg]:size-3.5", !shown && "opacity-70")}>
              {tab.icon}
            </span>
            <span className="min-w-0 truncate">{tab.title}</span>
            <button
              type="button"
              aria-label={`Close ${tab.title}`}
              tabIndex={-1}
              className={cn(
                "grid size-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground [&_svg]:size-3",
                shown ? "opacity-100" : "opacity-0 group-hover/tab:opacity-100",
              )}
              onClick={(event) => {
                event.stopPropagation();
                onClose(index);
              }}
            >
              <X />
            </button>
          </div>
        );
      })}
    </div>
  );
}
